import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect } from "react";
import { setCookie, removeCookie } from "@/lib/cookie";
import { getAccessTokenSilentlyRef } from "./getAccessTokenSilentlyRef";
import {
  authRecoveryRef,
  isUnrecoverableAuthError,
  rememberReturnTo,
  peekReturnTo,
  clearReturnTo,
} from "./authRecoveryRef";

/** One-shot guard so a hard-broken Auth0 config can't become a redirect loop. */
const RECOVERY_ATTEMPT_KEY = "ts_auth_recovery_attempt";

/**
 * Coalesces concurrent recovery attempts within a single page load.
 *
 * A dead refresh token surfaces from several places at once — Auth0CookieSync's
 * mint, and the ky client's beforeRequest for each in-flight query. Without
 * this, the first caller sets RECOVERY_ATTEMPT_KEY and the second immediately
 * reads it back, concludes "already tried and still failing", and drops the
 * user on the interactive sign-in card — pre-empting the silent redirect that
 * was about to succeed. The sessionStorage guard is meant to count distinct
 * page loads, not simultaneous callers within one.
 *
 * Module-level so it survives re-renders. Cleared when a mint succeeds.
 */
let recoveryInFlight: Promise<void> | null = null;

/** Test-only: module state outlives render(), so suites must reset it. */
export const _resetRecoveryInFlightForTesting = () => {
  recoveryInFlight = null;
};

/**
 * SP4c — Mounted inside <Auth0Provider> (via Auth0Bootstrap). Bridges Auth0's
 * authentication state to the existing `accessToken` cookie so the rest of
 * the app (ky client, Bearer header) keeps working unchanged.
 *
 * Side effects:
 *   1. On authenticated transition: mint an access token via
 *      getAccessTokenSilently() and write it to the cookie.
 *   2. On unauthenticated transition: remove the cookie.
 *   3. On mount: set the module-level getAccessTokenSilentlyRef so the ky
 *      client's beforeRetry hook can refresh tokens.
 *   4. On mount: set authRecoveryRef so non-React modules can trigger recovery
 *      when the refresh token is dead.
 *
 * Renders nothing.
 */
export function Auth0CookieSync(): null {
  const {
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    logout,
    loginWithRedirect,
  } = useAuth0();

  useEffect(() => {
    getAccessTokenSilentlyRef.current = getAccessTokenSilently;
  }, [getAccessTokenSilently]);

  const recover = useCallback(() => {
    // Everyone who notices the dead token in this load joins the same attempt.
    if (recoveryInFlight) return recoveryInFlight;

    // Capture the destination NOW, before logout() clears the session and the
    // router bounces us to /auth/signin.
    rememberReturnTo();

    recoveryInFlight = (async () => {
      // Already tried on a previous load in this tab and still failing — stop
      // redirecting and hand the user the interactive sign-in card instead of
      // looping forever.
      if (sessionStorage.getItem(RECOVERY_ATTEMPT_KEY)) {
        localStorage.setItem("ts_explicit_logout", "1");
        clearReturnTo();
        window.location.href = "/auth/signin";
        return;
      }
      sessionStorage.setItem(RECOVERY_ATTEMPT_KEY, "1");

      removeCookie("accessToken");
      // openUrl:false clears the SDK's poisoned localStorage cache (the dead
      // refresh token AND the stale ID token that keeps isAuthenticated true)
      // locally, with no round-trip to Auth0's /v2/logout — so the SSO session
      // survives and the redirect below usually returns without a prompt.
      await logout({ openUrl: false });
      await loginWithRedirect({ appState: { returnTo: peekReturnTo() } });
    })();

    return recoveryInFlight;
  }, [logout, loginWithRedirect]);

  useEffect(() => {
    authRecoveryRef.current = recover;
  }, [recover]);

  useEffect(() => {
    if (isLoading) return;

    if (isAuthenticated) {
      getAccessTokenSilently()
        .then((token) => {
          // Healthy again — release both guards.
          sessionStorage.removeItem(RECOVERY_ATTEMPT_KEY);
          recoveryInFlight = null;
          setCookie("accessToken", token);
        })
        .catch((err) => {
          if (isUnrecoverableAuthError(err)) {
            void recover();
            return;
          }
          // Network blip or similar — leave the session alone.
          console.warn("[Auth0CookieSync] failed to mint token:", err);
        });
    } else {
      removeCookie("accessToken");
    }
  }, [isAuthenticated, isLoading, getAccessTokenSilently, recover]);

  return null;
}
